import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from "react-bootstrap";
import SearchBar from "./searchBar";
import { Fragment, useEffect, useState } from "react";
import { ref, onValue, remove, update } from "firebase/database";
import { database } from "./firebase";
import {
  getAuth,
  updateEmail,
  updatePassword,
  deleteUser as deleteAuthUser,
  createUserWithEmailAndPassword,
} from "firebase/auth";
import DataTable from "react-data-table-component";
import TableStyleCard from "./tableStyleCard";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faEllipsisH,
  faKey,
  faUserCheck,
  faUserEdit,
  faUserSlash,
} from "@fortawesome/free-solid-svg-icons";
import { sendEmail } from "./sendEmail";

const Users = () => {
  const roles = ["admin", "user"];

  const [showModal, setShowModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [formData, setFormData] = useState({
    id: "",
    fullname: "",
    email: "",
    phone: "",
    department: "",
    role: "user",
  });
  const [authData, setAuthData] = useState({
    id: "",
    email: "",
    password: "",
  });

  const [showAddModal, setShowAddModal] = useState(false);

  const [newUser, setNewUser] = useState({
    fullname: "",
    email: "",
    phone: "",
    department: "",
    password: "",
  });

  /* ================= ADD USER ================= */
  const createUser = async (e) => {
    e.preventDefault();

    try {
      const auth = getAuth();

      const cred = await createUserWithEmailAndPassword(
        auth,
        newUser.email,
        newUser.password
      );

      const uid = cred.user.uid;

      await update(ref(database, `/helpdesk/users/${uid}`), {
        fullname: newUser.fullname,
        email: newUser.email,
        phone: newUser.phone,
        department: newUser.department,
        role: "user",
        createdAt: new Date().toISOString(),
      });

      // Build HTML email body
      const emailBody = `
          <p>Hello <strong>${newUser.fullname}</strong>,</p>

          <p>
            Your account has been created successfully on the 
            <strong>Greenwells Helpdesk Platform</strong>.
          </p>

          <p>
            You can now log in using the credentials below and start raising 
            departmental tickets, tracking requests, and managing support issues.
          </p>

          <table 
            border="1" 
            cellpadding="8" 
            cellspacing="0" 
            style="border-collapse: collapse; margin: 15px 0; width: 100%;"
          >
            <tr style="background-color: #f2f2f2;">
              <th align="left">Username / Email</th>
              <th align="left">Password</th>
            </tr>
            <tr>
              <td>${newUser.email}</td>
              <td>${newUser.password}</td>
            </tr>
          </table>

          <p>
            <strong>Login here:</strong><br/>
            <a href="https://helpdesk.greenwellsenergies.co.ke" target="_blank">
              https://helpdesk.greenwellsenergies.co.ke
            </a>
          </p>

          <p>
            We kindly encourage you to use this platform to raise departmental tickets 
            for faster assistance and better tracking of requests.
          </p>

          <p>
            Please keep your login details secure and do not share them with anyone.
          </p>

          <p>
            Regards,<br/>
            <strong>Greenwells IT Support Team</strong>
          </p>
        `;

      const emailResponse = sendEmail({
        subject: "Your Helpdesk Account Has Been Created",
        from: "support@helpdesk.greenwellsenergies.co.ke",
        to: [newUser.email],
        message: emailBody,
        isHtml: true,
      });

      console.log(emailResponse);

      setShowAddModal(false);
      setNewUser({
        fullname: "",
        email: "",
        phone: "",
        department: "",
        password: "",
      });

      alert("User created & email sent");
    } catch (err) {
      alert(err.message);
    }
  };

  // Fetch users and departments
  useEffect(() => {
    const usersRef = ref(database, "/helpdesk/users");
    const deptsRef = ref(database, "/helpdesk/departments");

    const unsubUsers = onValue(usersRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const list = Object.keys(data).map((id) => ({
          id,
          ...data[id],
        }));
        setUsers(list);
        setFilteredUsers(list);
      } else {
        setUsers([]);
        setFilteredUsers([]);
      }
    });

    const unsubDepts = onValue(deptsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const list = Object.keys(data).map((id) => ({
          id,
          ...data[id],
        }));
        setDepartments(list);
      } else {
        setDepartments([]);
      }
    });

    return () => {
      unsubUsers();
      unsubDepts();
    };
  }, []);

  // Search filter
  const handleSearch = (query) => {
    if (!query) return setFilteredUsers(users);
    const result = users.filter(
      (u) =>
        u.fullname?.toLowerCase().includes(query.toLowerCase()) ||
        u.email?.toLowerCase().includes(query.toLowerCase()) ||
        u.phone?.toLowerCase().includes(query.toLowerCase())
    );
    setFilteredUsers(result);
  };

  // Handle change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAuthChange = (e) => {
    const { name, value } = e.target;
    setAuthData((prev) => ({ ...prev, [name]: value }));
  };

  // Update normal user info
  const updateUserInfo = async (e) => {
    e.preventDefault();
    const userRef = ref(database, `/helpdesk/users/${formData.id}`);

    await update(userRef, {
      fullname: formData.fullname,
      phone: formData.phone,
      department: formData.department,
      role: formData.role,
    });

    setShowModal(false);
    alert("User details updated successfully!");
  };

  // Update email/password in Auth + DB
  const updateUserAuth = async (e) => {
    e.preventDefault();
    const userRef = ref(database, `/helpdesk/users/${authData.id}`);
    const authInstance = getAuth();
    const currentUser = authInstance.currentUser;

    try {
      // Update in Firebase Auth
      if (currentUser) {
        if (authData.email && authData.email !== currentUser.email) {
          await updateEmail(currentUser, authData.email);
        }
        if (authData.password) {
          await updatePassword(currentUser, authData.password);
        }
      }

      // Also update in Realtime DB
      await update(userRef, {
        email: authData.email,
        password: authData.password,
      });

      setShowAuthModal(false);
      alert("Login credentials updated successfully!");
    } catch (error) {
      console.error(error);
      alert("Error updating credentials. Check console for details.");
    }
  };

  // Edit info modal
  const startEditInfo = (user) => {
    setFormData(user);
    setShowModal(true);
  };

  // Edit credentials modal
  const startEditCredentials = (user) => {
    setAuthData({
      id: user.id,
      email: user.email,
      password: user.password || "",
      role: user.role || "user",
    });
    setShowAuthModal(true);
  };

  // Delete user (DB + Auth)
  const deleteUser = async (id, email) => {
    try {
      const userRef = ref(database, `/helpdesk/users/${id}`);
      await remove(userRef);

      const authInstance = getAuth();
      const currentUser = authInstance.currentUser;

      if (currentUser && currentUser.email === email) {
        await deleteAuthUser(currentUser);
      }

      alert("User deleted successfully!");
    } catch (error) {
      console.error(error);
      alert("Error deleting user.");
    }
  };

  //get user image
  const getUserImageByUid = (uid) => {
    const user = users.find((u) => u.id === uid);

    return user?.profilePicture
      ? user.profilePicture
      : `https://api.dicebear.com/7.x/avataaars/svg?seed=${uid}`;
  };

  // Expandable row
  const ExpandableComponent = ({ data }) => {
    const rows = [
      ["Full Name", data.fullname],
      ["Email", data.email],
      ["Phone", data.phone],
      ["Department", data.department],
      ["Role", data.role || "user"],
      ["Created At", data.createdAt || "—"],
    ];

    return (
      <div
        style={{
          padding: "15px",
          border: "1px solid #ddd",
          borderRadius: "8px",
          background: "#fff",
          marginBottom: "15px",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "150px 1fr",
            rowGap: "8px",
            columnGap: "10px",
          }}
        >
          {rows.map(([label, value], i) => (
            <Fragment key={i}>
              <div style={{ fontWeight: "bold" }}>{label}:</div>
              <div>{value || "-"}</div>
            </Fragment>
          ))}
        </div>
      </div>
    );
  };

  // Table columns
  const columns = [
    {
      name: "Name",
      sortable: true,
      cell: (row) => (
        <div
          className="bg-light p-2 rounded d-flex flex-column align-items-center"
          style={{ width: "100px" }}
        >
          <img
            src={getUserImageByUid(row.uid)}
            alt="User"
            width={48}
            height={48}
            className="rounded-circle mb-1 bg-white shadow p-1"
            style={{ objectFit: "cover" }}
          />

          <span
            className="text-gray-500 h5"
            style={{
              maxWidth: "100%",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              textAlign: "center",
            }}
            title={row.fullname} // shows full name on hover
          >
            {row.fullname}
          </span>
        </div>
      ),
    },

    {
      name: "Actions",
      cell: (row) => (
        <div className="dropdown">
          <button
            className="btn btn-round shadow-sm"
            type="button"
            data-toggle="dropdown"
            aria-expanded="false"
          >
            <FontAwesomeIcon icon={faEllipsisH} />
          </button>
          <div className="dropdown-menu dropdown-menu-right border-0 shadow-lg">
            <button
              className="dropdown-item"
              onClick={() => startEditInfo(row)}
            >
              <FontAwesomeIcon icon={faUserEdit} className="text-gray-300" />{" "}
              Edit Info
            </button>
            <button
              className="dropdown-item"
              onClick={() => startEditCredentials(row)}
            >
              <FontAwesomeIcon icon={faKey} className="text-gray-300" /> Update
              Credentials
            </button>
            <div className="dropdown-divider"></div>
            <button
              className="dropdown-item text-danger"
              onClick={() => deleteUser(row.id, row.email)}
            >
              <FontAwesomeIcon icon={faUserSlash} className="text-gray-300" />{" "}
              Delete
            </button>
          </div>
        </div>
      ),
      width: "90px",
    },
  ];

  return (
    <>
      <div className="card mt-4 shadow">
        <div className="card-header bg-white py-2 d-flex align-items-center">
          <div className="d-flex w-100">
            <SearchBar onSearch={handleSearch} />

            <button
              className="btn btn-lg btn-primary ml-auto"
              onClick={() => setShowAddModal(true)}
            >
              Add User
            </button>
          </div>
        </div>

        <div className="p-2" style={{ background: "#E1E3F0" }}>
          <DataTable
            customStyles={TableStyleCard}
            columns={columns}
            data={filteredUsers}
            pagination
            expandableRows
            expandableRowsComponent={ExpandableComponent}
            noTableHead
          />
        </div>
      </div>

      {/* Modal 1: Update user info */}
      <Modal show={showModal}>
        <ModalHeader>
          <ModalTitle>Update User Info</ModalTitle>
          <button className="btn btn-round" onClick={() => setShowModal(false)}>
            <span className="material-icons">close</span>
          </button>
        </ModalHeader>

        <ModalBody>
          <form onSubmit={updateUserInfo}>
            <input
              className="form-control mb-3"
              name="fullname"
              placeholder="Full Name"
              value={formData.fullname || ""}
              onChange={handleChange}
              required
            />
            <input
              className="form-control mb-3"
              name="phone"
              placeholder="Phone"
              value={formData.phone || ""}
              onChange={handleChange}
              required
            />
            <select
              className="form-control mb-3"
              name="department"
              value={formData.department || ""}
              onChange={handleChange}
              required
            >
              <option value="">Select Department</option>
              {departments.map((dept) => (
                <option key={dept.id} value={dept.name}>
                  {dept.name}
                </option>
              ))}
            </select>

            <select
              className="form-control mb-3"
              name="role"
              value={formData.role}
              onChange={handleChange}
              required
            >
              <option value="">Select Role</option>
              {roles.map((role) => (
                <option key={role} value={role}>
                  {role.charAt(0).toUpperCase() + role.slice(1)}
                </option>
              ))}
            </select>
          </form>
        </ModalBody>

        <ModalFooter>
          <button
            type="submit"
            onClick={updateUserInfo}
            className="btn btn-primary px-4"
          >
            Update
          </button>
        </ModalFooter>
      </Modal>

      {/* Modal 2: Update credentials */}
      <Modal show={showAuthModal}>
        <ModalHeader>
          <ModalTitle>Update Login Credentials</ModalTitle>
          <button
            className="btn btn-round"
            onClick={() => setShowAuthModal(false)}
          >
            <span className="material-icons">close</span>
          </button>
        </ModalHeader>

        <ModalBody>
          <form onSubmit={updateUserAuth}>
            <input
              className="form-control mb-3"
              name="email"
              type="email"
              placeholder="Email"
              value={authData.email || ""}
              onChange={handleAuthChange}
              required
            />
            <input
              className="form-control mb-3"
              name="password"
              type="password"
              placeholder="New Password"
              value={authData.password || ""}
              onChange={handleAuthChange}
            />
          </form>
        </ModalBody>

        <ModalFooter>
          <button
            type="submit"
            onClick={updateUserAuth}
            className="btn btn-primary px-4"
          >
            Update
          </button>
        </ModalFooter>
      </Modal>

      {/* ADD USER MODAL */}
      <Modal show={showAddModal}>
        <ModalHeader>
          <ModalTitle>
            <i className="fas fa-user-plus mr-2"></i>Add User
          </ModalTitle>

          <button
            className="btn btn-round"
            onClick={() => setShowAddModal(false)}
          >
            <span className="material-icons">close</span>
          </button>
        </ModalHeader>
        <ModalBody>
          <form onSubmit={createUser}>
            {["fullname", "email", "phone", "password"].map((f) => (
              <input
                key={f}
                className="form-control mb-3"
                type={f === "password" ? "password" : "text"}
                placeholder={f}
                onChange={(e) =>
                  setNewUser({ ...newUser, [f]: e.target.value })
                }
                required={f !== "phone"}
              />
            ))}
            <select
              className="form-control"
              onChange={(e) =>
                setNewUser({ ...newUser, department: e.target.value })
              }
              required
            >
              <option value="">Select Department</option>
              {departments.map((d) => (
                <option key={d.id}>{d.name}</option>
              ))}
            </select>
          </form>
        </ModalBody>
        <ModalFooter>
          <button className="btn btn-primary" onClick={createUser}>
            <i className="fas fa-save mr-2"></i>Create
          </button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default Users;
